import React, { useEffect, useMemo } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';

import {
  delhiveryInformationValidation,
  onForward,
  PaymentModeOptions,
  PickupLocationTypeOptions,
  ShippingModeOptions,
} from './constants';

interface Props {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const DelhiveryInformation: React.FC<Props> = ({
  onClickForward,
  onClose,
  initialStates,
  isEditable = false,
  showFedExDetails,
  btnText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount, delhiveryDetails } = useSelector((state: RootStateType) => state.adminShipment);

  const delhiverySettings = useSelector((state: RootStateType) => state.user.carrierSetting.delhivery);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      accountNo: '',
      pickup_location: '',
      payment_mode: '',
      shipping_mode: '',
      ...initialStates,
    },
    validationSchema: delhiveryInformationValidation,
    onSubmit: values => {
      if (!isWarehouseSelectedINFedX || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const { values, setFieldValue, errors, handleSubmit } = formik;

  useEffect(() => {
    if (values.accountNo?.length && accountOptions?.length) {
      const matched = accountOptions.find(opt => opt.value.startsWith(values.accountNo));

      if (matched) {
        setFieldValue('accountNo', matched.value);
      }
    }
  }, [values.accountNo, accountOptions]);

  if (!showFedExDetails) return null;

  return (
    <Paper variant="outlined" sx={{ my: '2rem', p: '2rem' }}>
      <Typography variant="h6" gutterBottom>
        Delhivery Information
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="accountNo"
            label="Delhivery Account"
            required
            placeholder="Select"
            name="accountNo"
            value={values.accountNo ? [values.accountNo] : []}
            options={delhiverySettings?.accountNo?.length ? delhiverySettings?.accountNo : accountOptions}
            onChange={value => {
              setFieldValue('accountNo', value[0]);
            }}
            error={errors.accountNo as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="shipping_mode"
            label="Shipping Mode"
            required
            placeholder="Select"
            name="shipping_mode"
            value={values.shipping_mode ? [values.shipping_mode] : []}
            options={ShippingModeOptions}
            onChange={val => setFieldValue('shipping_mode', val[0] || '')}
            error={errors.shipping_mode as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="payment_mode"
            label="Payment mode"
            required
            placeholder="Select"
            name="payment_mode"
            value={values.payment_mode ? [values.payment_mode] : []}
            options={PaymentModeOptions}
            onChange={val => setFieldValue('payment_mode', val[0] || '')}
            error={errors.payment_mode as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="pickup_location"
            label="Pickup location"
            required
            placeholder="Select"
            name="pickup_location"
            value={values.pickup_location ? [values.pickup_location] : []}
            options={PickupLocationTypeOptions}
            onChange={val => setFieldValue('pickup_location', val[0] || '')}
            error={errors.pickup_location as string}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SbButton
            disabled={!isEditable}
            variant="contained"
            sx={{ borderRadius: 1 }}
            fullWidth
            onClick={() => handleSubmit()}
          >
            {btnText}
          </SbButton>
        </Grid>

        {onClose && (
          <Grid item xs={12} sm={6} md={3}>
            <SbButton variant="outlined" sx={{ borderRadius: 1 }} fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>
        )}
      </Grid>

      {delhiveryDetails?.response && (
        <Grid container mt={3}>
          <Grid item xs="auto" border="1px solid" className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component="span"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
                ml: 1,
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default DelhiveryInformation;
